import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Users, Heart, Check } from "lucide-react";
import { api, getInterestedSet } from "../lib/api";
import { CATEGORY_LABEL, formatEventDate } from "../lib/categories";
import InterestModal from "../components/InterestModal";

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interested, setInterested] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`)
      .then((r) => {
        setEvent(r.data);
        setInterested(getInterestedSet().has(r.data.id));
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 pb-20" data-testid="event-loading">Loading…</div>;
  }
  if (!event) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 pb-20" data-testid="event-not-found">
        <h1 className="font-serif text-4xl">Not found.</h1>
        <p className="mt-3" style={{ color: "var(--text-muted)" }}>
          This gathering may have wrapped up. <Link to="/events" className="underline">Browse all →</Link>
        </p>
      </div>
    );
  }

  const { date, time } = formatEventDate(event.starts_at);

  return (
    <div data-testid="event-detail-page" className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-20">
      <Link to="/events" className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="back-to-events">
        <ArrowLeft size={16} /> Back to gatherings
      </Link>

      <div className="mt-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="card-warm overflow-hidden">
            <img src={event.cover_image} alt={event.title} className="w-full aspect-[4/3] object-cover" />
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="label-eyebrow">{CATEGORY_LABEL[event.category]} · {event.city}</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight" style={{ color: "var(--text-main)" }}>
            {event.title}
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
            Hosted by <span style={{ color: "var(--text-main)" }}>{event.host_name}</span>
          </p>

          <div className="mt-7 space-y-3 text-[15px]">
            <div className="flex items-center gap-3"><Calendar size={17} style={{ color: "var(--primary)" }} /> <span>{date} · {time}</span></div>
            <div className="flex items-center gap-3"><MapPin size={17} style={{ color: "var(--primary)" }} /> <span>{event.venue}, {event.city}</span></div>
            <div className="flex items-center gap-3"><Users size={17} style={{ color: "var(--primary)" }} /> <span>{event.capacity} seats · {event.duration_minutes} mins</span></div>
            <div className="flex items-center gap-3"><Heart size={17} style={{ color: "var(--primary)" }} /> <span>{event.interested_count || 0} people interested</span></div>
          </div>

          <button
            onClick={() => !interested && setShowModal(true)}
            disabled={interested}
            data-testid="im-interested-btn"
            className={interested ? "btn-secondary mt-8 w-full justify-center" : "btn-primary mt-8 w-full justify-center"}
          >
            {interested ? (<><Check size={18} /> You're on the list</>) : (<><Heart size={18} /> RSVP — I'm interested</>)}
          </button>
          <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
            We reach out on the contact you leave with us — venue, timing and any specifics shared one-to-one.
          </p>
        </div>
      </div>

      <div className="mt-14 max-w-3xl">
        <div className="label-eyebrow">About this gathering</div>
        <div className="mt-4 font-serif text-xl md:text-2xl leading-relaxed whitespace-pre-line" style={{ color: "var(--text-main)" }}>
          {event.description}
        </div>
      </div>

      {showModal && (
        <InterestModal
          event={event}
          onClose={() => setShowModal(false)}
          onSuccess={(count) => {
            setInterested(true);
            setEvent((e) => ({ ...e, interested_count: count }));
          }}
        />
      )}
    </div>
  );
}
